import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@kmgestao/ui';

import { useSendOnboardingLink, type DuplicateLead } from '../../../features/leads/index.js';
import { useDefaultOnboardingTemplate } from '../../../features/onboarding/index.js';

import { LeadStageBadge } from './LeadStageBadge.js';

export interface DuplicateLeadDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Lead existente que bateu o email digitado pela Karla. */
  lead: DuplicateLead;
}

/**
 * Dialog mostrado quando Karla tenta cadastrar lead com email que já
 * existe como lead ativo. Oferece 3 caminhos claros para não forçar
 * escolha prematura:
 *
 *   - **Abrir lead existente**: navega para `/superadmin/leads/:id`
 *   - **Reenviar questionário**: dispara `send-onboarding-link` com
 *     o lead existente (útil quando lead já estava em `form_sent` mas
 *     Karla quer reforçar ou o link expirou)
 *   - **Cancelar**: fecha, dados do form ficam preservados (Karla
 *     pode ajustar o email e tentar de novo)
 *
 * **Por que não "criar mesmo assim"**: regra de produto — email único
 * em leads ativos. Banco também rejeita via unique index.
 */
export function DuplicateLeadDialog({ open, onOpenChange, lead }: DuplicateLeadDialogProps) {
  const navigate = useNavigate();
  const sendOnboardingLink = useSendOnboardingLink();
  const defaultTemplate = useDefaultOnboardingTemplate();

  const createdAt = formatDate(lead.created_at);

  const handleOpenExisting = () => {
    onOpenChange(false);
    navigate(`/superadmin/leads/${lead.id}`);
  };

  const handleResend = async () => {
    if (!defaultTemplate.data) {
      toast.error('Template padrão não disponível. Publique um em Configurações → Onboarding.');
      return;
    }
    try {
      const result = await sendOnboardingLink.mutateAsync({
        lead_id: lead.id,
        template_id: defaultTemplate.data.id,
      });
      if (result.email_status === 'sent') {
        toast.success(`Questionário reenviado para ${lead.email}.`);
      } else {
        toast.warning(
          `Submission criada, mas email falhou (${result.email_status}). Acesse o lead para tentar de novo.`,
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error('Falha ao reenviar', { description: (err as Error).message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Já existe um lead com esse email</DialogTitle>
          <DialogDescription>
            Em vez de criar um segundo registro, abra o lead existente ou reenvie o questionário
            para reforçar o contato.
          </DialogDescription>
        </DialogHeader>

        {/* Bloco do lead existente: usa border-l accent (em vez de card
            aninhado pesado) pra dar destaque sem competir com o dialog.
            Stage badge fica abaixo do nome (não ao lado) — alguns labels
            são longos ("Contrato enviado") e quebravam o equilíbrio
            visual quando ficavam side-by-side. */}
        <div className="space-y-1.5 border-l-2 border-accent/40 pl-4 py-1">
          <p className="text-base font-semibold text-foreground">{lead.full_name}</p>
          <div className="flex flex-wrap items-center gap-2">
            <LeadStageBadge stage={lead.stage} />
            {createdAt ? (
              <span className="text-xs text-muted-foreground">desde {createdAt}</span>
            ) : null}
          </div>
          {lead.clinic_name_guess ? (
            <p className="text-sm text-muted-foreground">{lead.clinic_name_guess}</p>
          ) : null}
          <p className="text-xs text-muted-foreground">{lead.email}</p>
        </div>

        {/* Footer com hierarquia clara: ação principal (Abrir lead) à
            direita em destaque; ações secundárias agrupadas à esquerda.
            Removido size="lg" — o default já é grande o suficiente
            dentro de um sm:max-w-lg dialog. */}
        <DialogFooter className="gap-2 sm:justify-between">
          <DialogClose asChild>
            <Button type="button" variant="ghost" className="w-full sm:w-auto">
              Cancelar
            </Button>
          </DialogClose>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleResend}
              disabled={sendOnboardingLink.isPending || defaultTemplate.isLoading}
            >
              {sendOnboardingLink.isPending ? 'Reenviando…' : 'Reenviar questionário'}
            </Button>
            <Button type="button" className="w-full sm:w-auto" onClick={handleOpenExisting}>
              Abrir lead existente
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string | null {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}
